import { useCallback, useMemo, useState } from 'react';
import { useCustom, useCustomMutation } from '@refinedev/core';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  DoorOpen,
  GripVertical,
  Loader2,
  LogIn,
  MessageSquare,
  Mic,
  MicOff,
  MonitorUp,
  Send,
  Users,
  Video,
  VideoOff,
  X,
  type LucideIcon,
} from 'lucide-react';
import { notify } from '@/lib/notify';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/components/ui/table';
import { InteractionsAdmin } from './interactions-admin';

interface BreakoutParticipant {
  userId: number;
  firstName: string | null;
  username: string | null;
  avatarUrl: string | null;
  live: boolean;
  mic: boolean;
  camera: boolean;
  screen: boolean;
}

interface BreakoutPreviewRoom {
  roomIndex: number;
  label: string;
  participants: Array<{
    userId: number;
    firstName: string | null;
    username: string | null;
    avatarUrl: string | null;
  }>;
}

interface BreakoutPreviewResult {
  mode: string;
  roomCount: number;
  viewerCount: number;
  rooms: BreakoutPreviewRoom[];
}

interface BreakoutStatusRoom {
  roomIndex: number;
  label: string;
  callRoomId: string;
  participants: BreakoutParticipant[];
}

interface BreakoutStatus {
  session: { id: string; status: string; mode: string; openedAt: string } | null;
  rooms: BreakoutStatusRoom[];
}

interface BreakoutAssignmentInput {
  userId: number;
  roomIndex: number;
}

interface OpenBreakoutDto {
  livestreamId: number;
  mode: 'AUTO' | 'MANUAL';
  roomCount?: number;
  groupSize?: number;
  assignments?: BreakoutAssignmentInput[];
}

interface MessageBreakoutDto {
  livestreamId: number;
  text: string;
  targetRoomIndex?: number;
  displayMode?: 'toast' | 'fullscreen';
}

interface LivestreamBreakoutPanelProps {
  livestreamId: number;
  isLive: boolean;
}

function displayName(p: BreakoutParticipant): string {
  const full = [p.firstName].filter(Boolean).join(' ').trim();
  if (full) {
    return full;
  }
  if (p.username) {
    return `@${p.username}`;
  }
  return `#${p.userId}`;
}

/**
 * Единая шапка модалки: иконка в кружке + заголовок + подзаголовок.
 * Один стиль для всех breakout-диалогов (CLAUDE.md: переиспользовать аналог).
 */
function ModalHead({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <DialogHeader>
      <div className="flex items-start gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Icon className="size-4" />
        </span>
        <div className="space-y-1">
          <DialogTitle className="text-base leading-tight">{title}</DialogTitle>
          <DialogDescription className="text-sm leading-snug">
            {description}
          </DialogDescription>
        </div>
      </div>
    </DialogHeader>
  );
}



interface PreviewParticipant {
  userId: number;
  firstName: string | null;
  username: string | null;
}

function previewName(p: PreviewParticipant): string {
  return p.firstName?.trim() || (p.username ? `@${p.username}` : `#${p.userId}`);
}

// ─── DnD для MANUAL-режима в превью-модалке ────────────────────────
function PreviewParticipantCard({ participant }: { participant: PreviewParticipant }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `pv-user-${participant.userId}`,
    data: { userId: participant.userId },
  });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={`flex cursor-grab items-center gap-2 rounded-md border bg-card px-2.5 py-1.5 text-sm active:cursor-grabbing ${
        isDragging ? 'opacity-40' : ''
      }`}
    >
      <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="flex-1 truncate">{previewName(participant)}</span>
    </div>
  );
}

// ─── Распределение: зал = таблица, строки draggable между залами ────
function MediaStatus({ p }: { p: BreakoutParticipant }) {
  if (!p.live) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className="size-2 shrink-0 rounded-full bg-zinc-400" />
        Не подключён
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5">
      <span
        className="size-2 shrink-0 rounded-full bg-green-500"
        title="В зале"
      />
      {p.mic ? (
        <Mic className="h-3.5 w-3.5 text-green-500" />
      ) : (
        <MicOff className="h-3.5 w-3.5 text-zinc-400" />
      )}
      {p.camera ? (
        <Video className="h-3.5 w-3.5 text-green-500" />
      ) : (
        <VideoOff className="h-3.5 w-3.5 text-zinc-400" />
      )}
      {p.screen && <MonitorUp className="h-3.5 w-3.5 text-blue-500" />}
    </span>
  );
}

function DraggableMemberRow({ p }: { p: BreakoutParticipant }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `member-${p.userId}`,
    data: { userId: p.userId },
  });
  return (
    <TableRow
      ref={setNodeRef}
      className={isDragging ? 'opacity-40' : ''}
    >
      <TableCell className="w-8 cursor-grab active:cursor-grabbing" {...attributes} {...listeners}>
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </TableCell>
      <TableCell className="font-medium">{displayName(p)}</TableCell>
      <TableCell className="w-36">
        <MediaStatus p={p} />
      </TableCell>
    </TableRow>
  );
}

function RoomTable({
  room,
}: {
  room: BreakoutStatusRoom;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `room-${room.roomIndex}`,
    data: { roomIndex: room.roomIndex },
  });
  const enterRoom = (): void => {
    window.open(
      `/livestreams/stage/${room.callRoomId}?label=${encodeURIComponent(room.label)}`,
      '_blank',
      'noopener',
    );
  };
  return (
    <div
      ref={setNodeRef}
      className={`overflow-hidden rounded-lg border transition-colors ${
        isOver ? 'border-primary bg-accent/40' : 'bg-muted/20'
      }`}
    >
      <div className="flex items-center gap-2 border-b bg-muted/40 px-3 py-2">
        <span className="flex-1 text-sm font-medium">{room.label}</span>
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
          {room.participants.length}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          title="Войти в зал"
          onClick={enterRoom}
        >
          <LogIn className="h-3.5 w-3.5" />
        </Button>
      </div>
      {room.participants.length === 0 ? (
        <div className="py-6 text-center text-xs text-muted-foreground">
          Перетащите участников сюда
        </div>
      ) : (
        <Table>
          <TableBody>
            {room.participants.map((p) => (
              <DraggableMemberRow key={p.userId} p={p} />
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function PreviewRoomColumn({
  roomIndex,
  label,
  participants,
}: {
  roomIndex: number;
  label: string;
  participants: PreviewParticipant[];
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `pv-room-${roomIndex}`,
    data: { roomIndex },
  });
  return (
    <div className="rounded-lg border bg-muted/30">
      <div className="flex items-center gap-2 border-b p-2.5">
        <span className="flex-1 text-sm font-medium">{label}</span>
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
          {participants.length}
        </span>
      </div>
      <div
        ref={setNodeRef}
        className={`min-h-[80px] space-y-1.5 p-2 transition-colors ${
          isOver ? 'bg-accent/50' : ''
        }`}
      >
        {participants.length === 0 ? (
          <div className="py-4 text-center text-xs text-muted-foreground">Пусто</div>
        ) : (
          participants.map((p) => (
            <PreviewParticipantCard key={p.userId} participant={p} />
          ))
        )}
      </div>
    </div>
  );
}

export function LivestreamBreakoutPanel({ livestreamId, isLive }: LivestreamBreakoutPanelProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const [interactionsOpen, setInteractionsOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState<BreakoutPreviewResult | null>(null);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  // Режим создания: AUTO — авто-раскладка по числу залов/группе;
  // MANUAL — админ сам распределяет участников по залам в превью (DnD).
  const [createMode, setCreateMode] = useState<'AUTO' | 'MANUAL'>('AUTO');
  const [autoBy, setAutoBy] = useState<'rooms' | 'group'>('group');
  const [roomCount, setRoomCount] = useState(2);
  const [groupSize, setGroupSize] = useState(2);
  // Редактируемая раскладка в превью-модалке (для MANUAL drag): userId → roomIndex.
  const [previewAssign, setPreviewAssign] = useState<Map<number, number>>(() => new Map());
  const [previewActiveUserId, setPreviewActiveUserId] = useState<number | null>(null);
  const [messageOpen, setMessageOpen] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [messageTarget, setMessageTarget] = useState<string>('all');
  // Режим показа сообщения участнику: toast (баннер) / fullscreen (на весь экран).
  const [messageDisplayMode, setMessageDisplayMode] = useState<'toast' | 'fullscreen'>(
    'toast',
  );
  // Зал-цель для нового интерактива: 'all' → всем залам сессии,
  // иначе roomIndex (передаётся как targetRoomIndex в create).
  const [interactionTarget, setInteractionTarget] = useState<string>('all');

  // Realtime через WS (useLivestreamRealtime на странице show.tsx инвалидирует
  // этот queryKey по livestream.breakout.changed). Поллинг — редкий fallback
  // на случай пропущенного WS-сообщения, пауза во время drag.
  const { result: statusResult, query: statusQuery } = useCustom<BreakoutStatus>({
    url: `/admin/breakout/status/${livestreamId}`,
    method: 'get',
    queryOptions: {
      // Realtime через WS (useLivestreamRealtime инвалидирует этот queryKey
      // по livestream.breakout.changed). Поллинг — редкий fallback.
      enabled: isLive,
      refetchInterval: 5 * 60_000,
    },
  });
  // Каст к именованному интерфейсу (Refine result.data — широкий тип).
  // Консистентно с livestream-call-stage.tsx; хук разрешает as <NamedType>.
  const status = statusResult?.data as BreakoutStatus | undefined;
  const session = status?.session ?? null;
  const rooms = useMemo(() => status?.rooms ?? [], [status]);

  const { mutate: previewBreakout, mutation: previewMutation } = useCustomMutation();
  const { mutate: openBreakout, mutation: openMutation } = useCustomMutation();
  const { mutate: closeBreakout, mutation: closeMutation } = useCustomMutation();
  const { mutate: messageBreakout, mutation: messageMutation } = useCustomMutation();
  const { mutate: reassign, mutation: reassignMutation } = useCustomMutation();

  const refetchStatus = useCallback(() => {
    void statusQuery?.refetch();
  }, [statusQuery]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  // Параметры для preview-запроса. AUTO — по числу залов/группе. MANUAL —
  // mode:'MANUAL' с начальной раскладкой (все в зал 1), дальше админ
  // перетаскивает участников в превью.
  const buildPreviewValues = useCallback(
    (manualAssign?: BreakoutAssignmentInput[]): OpenBreakoutDto => {
      if (createMode === 'MANUAL') {
        return {
          livestreamId,
          mode: 'MANUAL',
          roomCount,
          assignments:
            manualAssign && manualAssign.length > 0
              ? manualAssign
              : // Стартовая раскладка: число залов задаём «якорными» пустыми
                // назначениями нет — backend требует >=1 assignment, поэтому
                // первый запрос делаем AUTO-подобным: roomCount фиксируем,
                // участников backend разложит round-robin для отображения,
                // далее редактируем перетаскиванием.
                undefined,
        };
      }
      const values: OpenBreakoutDto = { livestreamId, mode: 'AUTO' };
      if (autoBy === 'rooms') {
        values.roomCount = roomCount;
      } else {
        values.groupSize = groupSize;
      }
      return values;
    },
    [autoBy, createMode, groupSize, livestreamId, roomCount],
  );

  // Шаг 1: dry-run. Ничего не создаётся. MANUAL: первый расчёт идёт через
  // AUTO round-robin (backend), его раскладку кладём в редактируемый
  // previewAssign — админ затем перетаскивает участников между залами.
  const handlePreview = useCallback(() => {
    const previewValues: OpenBreakoutDto =
      createMode === 'MANUAL'
        ? { livestreamId, mode: 'AUTO', roomCount }
        : buildPreviewValues();
    previewBreakout(
      { url: '/admin/breakout/preview', method: 'post', values: previewValues },
      {
        onSuccess: (data) => {
          const result = data?.data as BreakoutPreviewResult | undefined;
          if (!result) {
            notify.error('Не удалось рассчитать распределение');
            return;
          }
          setPreview(result);
          // Инициализируем редактируемую раскладку из превью.
          const initial = new Map<number, number>();
          for (const room of result.rooms) {
            for (const p of room.participants) {
              initial.set(p.userId, room.roomIndex);
            }
          }
          setPreviewAssign(initial);
          setCreateOpen(false);
          setPreviewOpen(true);
        },
        onError: (err) => notify.error(err.message || 'Не удалось рассчитать распределение'),
      },
    );
  }, [buildPreviewValues, createMode, livestreamId, previewBreakout, roomCount]);

  // Шаг 2: применить раскладку. AUTO — те же параметры. MANUAL — собранные
  // из previewAssign назначения (с учётом перетаскиваний админа).
  const handleConfirmOpen = useCallback(() => {
    let values: OpenBreakoutDto;
    if (createMode === 'MANUAL') {
      const assignments: BreakoutAssignmentInput[] = [...previewAssign.entries()].map(
        ([userId, idx]) => ({ userId, roomIndex: idx }),
      );
      if (assignments.length === 0) {
        notify.error('Нет участников для распределения');
        return;
      }
      values = { livestreamId, mode: 'MANUAL', roomCount, assignments };
    } else {
      values = buildPreviewValues();
    }
    openBreakout(
      { url: '/admin/breakout/open', method: 'post', values },
      {
        onSuccess: () => {
          notify.success('Сессионные залы открыты');
          setPreviewOpen(false);
          setPreview(null);
          refetchStatus();
        },
        onError: (err) => notify.error(err.message || 'Не удалось открыть залы'),
      },
    );
  }, [buildPreviewValues, createMode, livestreamId, openBreakout, previewAssign, refetchStatus, roomCount]);

  const handleClose = useCallback(() => {
    closeBreakout(
      { url: '/admin/breakout/close', method: 'post', values: { livestreamId } },
      {
        onSuccess: () => {
          notify.success('Залы закрыты, участники возвращены на трансляцию');
          setConfirmCloseOpen(false);
          refetchStatus();
        },
        onError: (err) => notify.error(err.message || 'Не удалось закрыть залы'),
      },
    );
  }, [closeBreakout, livestreamId, refetchStatus]);

  // Профили всех участников из превью (userId → имя), для рендера DnD.
  const previewParticipants = useMemo(() => {
    const map = new Map<number, PreviewParticipant>();
    for (const room of preview?.rooms ?? []) {
      for (const p of room.participants) {
        map.set(p.userId, { userId: p.userId, firstName: p.firstName, username: p.username });
      }
    }
    return map;
  }, [preview]);

  // Раскладка для рендера превью-модалки из редактируемого previewAssign.
  const previewRooms = useMemo(() => {
    const count = preview?.roomCount ?? roomCount;
    const rooms: { roomIndex: number; label: string; participants: PreviewParticipant[] }[] =
      [];
    for (let i = 1; i <= count; i++) {
      rooms.push({ roomIndex: i, label: `Зал ${i}`, participants: [] });
    }
    for (const [userId, idx] of previewAssign.entries()) {
      const room = rooms[idx - 1];
      const profile = previewParticipants.get(userId);
      if (room && profile) {
        room.participants.push(profile);
      }
    }
    return rooms;
  }, [preview, previewAssign, previewParticipants, roomCount]);

  const handlePreviewDragEnd = useCallback((e: DragEndEvent) => {
    setPreviewActiveUserId(null);
    const { active, over } = e;
    if (!over) {
      return;
    }
    const userId = active.data.current?.userId;
    const targetRoomIndex = over.data.current?.roomIndex;
    if (typeof userId !== 'number' || typeof targetRoomIndex !== 'number') {
      return;
    }
    setPreviewAssign((prev) => {
      if (prev.get(userId) === targetRoomIndex) {
        return prev;
      }
      const next = new Map(prev);
      next.set(userId, targetRoomIndex);
      return next;
    });
  }, []);

  const previewActiveParticipant = useMemo(
    () =>
      previewActiveUserId === null
        ? null
        : previewParticipants.get(previewActiveUserId) ?? null,
    [previewActiveUserId, previewParticipants],
  );

  const handleSendMessage = useCallback(() => {
    const text = messageText.trim();
    if (!text) {
      return;
    }
    const values: MessageBreakoutDto = {
      livestreamId,
      text,
      displayMode: messageDisplayMode,
    };
    if (messageTarget !== 'all') {
      values.targetRoomIndex = Number(messageTarget);
    }
    messageBreakout(
      { url: '/admin/breakout/message', method: 'post', values },
      {
        onSuccess: () => {
          notify.success('Сообщение отправлено');
          setMessageText('');
          setMessageOpen(false);
        },
        onError: (err) => notify.error(err.message || 'Не удалось отправить сообщение'),
      },
    );
  }, [livestreamId, messageBreakout, messageDisplayMode, messageText, messageTarget]);

  // Переназначение через селектор зала в таблице (тот же эндпойнт что DnD).
  const handleReassignSelect = useCallback(
    (userId: number, targetRoomIndex: number) => {
      if (!session || reassignMutation.isPending) {
        return;
      }
      const sourceRoom = rooms.find((r) =>
        r.participants.some((p) => p.userId === userId),
      );
      if (sourceRoom && sourceRoom.roomIndex === targetRoomIndex) {
        return;
      }
      reassign(
        {
          url: '/admin/breakout/reassign',
          method: 'post',
          values: { sessionId: session.id, userId, targetRoomIndex },
        },
        {
          onSuccess: () => refetchStatus(),
          onError: (err) => notify.error(err.message || 'Не удалось переместить участника'),
        },
      );
    },
    [reassign, reassignMutation.isPending, refetchStatus, rooms, session],
  );

  const totalAssigned = useMemo(
    () => rooms.reduce((sum, r) => sum + r.participants.length, 0),
    [rooms],
  );

  // DnD: перетащили участника на другой зал → reassign (та же логика).
  const handleBoardDragEnd = useCallback(
    (e: DragEndEvent) => {
      const userId = e.active.data.current?.userId;
      const targetRoomIndex = e.over?.data.current?.roomIndex;
      if (typeof userId === 'number' && typeof targetRoomIndex === 'number') {
        handleReassignSelect(userId, targetRoomIndex);
      }
    },
    [handleReassignSelect],
  );

  if (!isLive) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Сессионные залы доступны только во время активной трансляции.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Слева — таблица распределения, справа узкая колонка управления */}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
        {/* ЛЕВО: каждый зал — отдельная таблица, строки DnD между залами */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              Распределение участников
              {session && totalAssigned > 0 && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
                  {totalAssigned}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!session ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Сессионные залы не открыты.
              </div>
            ) : rooms.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Залы ещё создаются…
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragEnd={handleBoardDragEnd}
              >
                <div className="space-y-3">
                  {rooms.map((room) => (
                    <RoomTable key={room.callRoomId} room={room} />
                  ))}
                </div>
                <DragOverlay />
              </DndContext>
            )}
          </CardContent>
        </Card>

        {/* ПРАВО: компактное управление сессией */}
        <div className="space-y-3">
          <Card>
            <CardContent className="space-y-3 pt-4">
              <div className="flex items-center gap-2">
                <DoorOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="text-sm font-medium">Сессионные залы</span>
              </div>
              {session ? (
                <>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="default"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Открыты
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {rooms.length} зал(ов) ·{' '}
                      {session.mode === 'AUTO' ? 'авто' : 'ручной'}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {rooms.map((r) => (
                      <div
                        key={r.callRoomId}
                        className="flex items-center gap-2 rounded-md border bg-card px-2.5 py-1.5 text-xs"
                      >
                        <span className="flex-1 truncate font-medium">{r.label}</span>
                        <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                          {r.participants.length}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          title="Войти в зал"
                          onClick={() =>
                            window.open(
                              `/livestreams/stage/${r.callRoomId}?label=${encodeURIComponent(r.label)}`,
                              '_blank',
                              'noopener',
                            )
                          }
                        >
                          <LogIn className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="h-8 w-full text-xs"
                    disabled={closeMutation.isPending}
                    onClick={() => setConfirmCloseOpen(true)}
                  >
                    <X className="mr-1 h-3 w-3" />
                    Закрыть залы
                  </Button>
                </>
              ) : (
                <>
                  <Badge variant="secondary">Не открыты</Badge>
                  <Button
                    variant="default"
                    size="sm"
                    className="h-8 w-full text-xs"
                    onClick={() => setCreateOpen(true)}
                  >
                    <DoorOpen className="mr-1 h-3 w-3" />
                    Создать сессионные залы
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {session && (
            <Card>
              <CardContent className="space-y-2 pt-4">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="text-sm font-medium">Сообщение на экран</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Текст участникам залов — баннером или на весь экран.
                </p>
                <Button
                  size="sm"
                  className="h-8 w-full text-xs"
                  onClick={() => setMessageOpen(true)}
                >
                  <Send className="mr-1 h-3 w-3" />
                  Отправить сообщение
                </Button>
              </CardContent>
            </Card>
          )}

          {session && (
            <Card>
              <CardContent className="space-y-2 pt-4">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="text-sm font-medium">Интерактивы зала</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Опросы и сборы ответов — отдельно от эфира.
                </p>
                <Button
                  size="sm"
                  className="h-8 w-full text-xs"
                  onClick={() => setInteractionsOpen(true)}
                >
                  <Users className="mr-1 h-3 w-3" />
                  Открыть интерактивы
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Модалка «Сообщение на экран» */}
      <Dialog open={messageOpen} onOpenChange={setMessageOpen}>
        <DialogContent className="max-w-lg">
          <ModalHead
            icon={MessageSquare}
            title="Сообщение на экран"
            description="Текст увидят участники выбранных залов."
          />
          <div className="space-y-4 py-1">
            <Textarea
              placeholder="Текст, который увидят участники в залах…"
              value={messageText}
              maxLength={500}
              onChange={(e) => setMessageText(e.target.value)}
              rows={4}
            />
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Кому показать</Label>
              <Select value={messageTarget} onValueChange={setMessageTarget}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Всем залам</SelectItem>
                  {rooms.map((r) => (
                    <SelectItem key={r.callRoomId} value={String(r.roomIndex)}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Как показать</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={messageDisplayMode === 'toast' ? 'default' : 'outline'}
                  className="h-8 flex-1 text-xs"
                  onClick={() => setMessageDisplayMode('toast')}
                >
                  Баннер
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={messageDisplayMode === 'fullscreen' ? 'default' : 'outline'}
                  className="h-8 flex-1 text-xs"
                  onClick={() => setMessageDisplayMode('fullscreen')}
                >
                  На весь экран
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {messageDisplayMode === 'toast'
                  ? 'Ненавязчивый баннер сверху, авто-скрытие через 8с.'
                  : 'Крупный оверлей на весь экран зала, перекрывает видео.'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMessageOpen(false)}>
              Отмена
            </Button>
            <Button
              disabled={messageMutation.isPending || messageText.trim().length === 0}
              onClick={handleSendMessage}
            >
              <Send className="mr-1 h-4 w-4" />
              Отправить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Модалка интерактивов зала */}
      <Dialog open={interactionsOpen} onOpenChange={setInteractionsOpen}>
        <DialogContent className="max-h-[90vh] w-[95vw] !max-w-[1100px] overflow-y-auto">
          <DialogHeader className="sr-only">
            <DialogTitle>Интерактивы зала</DialogTitle>
            <DialogDescription>
              Опросы и сборы ответов внутри сессионных залов — отдельно от эфира.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">Новый интерактив для:</span>
              <Select value={interactionTarget} onValueChange={setInteractionTarget}>
                <SelectTrigger className="h-9 w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Всех залов</SelectItem>
                  {rooms.map((r) => (
                    <SelectItem key={r.callRoomId} value={String(r.roomIndex)}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <InteractionsAdmin
              bare
              baseUrl={`/admin/breakout/livestreams/${livestreamId}`}
              title="Интерактивы зала"
              description="Опросы и сборы ответов внутри сессионных залов — отдельно от эфира."
              createExtraValues={
                interactionTarget === 'all'
                  ? {}
                  : { targetRoomIndex: Number(interactionTarget) }
              }
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Диалог создания */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <ModalHead
            icon={DoorOpen}
            title="Создать сессионные залы"
            description="Сначала вы увидите превью распределения. Залы создаются и участники переносятся только после подтверждения."
          />
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant={createMode === 'AUTO' ? 'default' : 'outline'}
                className="h-8 flex-1 text-xs"
                onClick={() => setCreateMode('AUTO')}
              >
                Авто-распределение
              </Button>
              <Button
                type="button"
                size="sm"
                variant={createMode === 'MANUAL' ? 'default' : 'outline'}
                className="h-8 flex-1 text-xs"
                onClick={() => setCreateMode('MANUAL')}
              >
                Вручную
              </Button>
            </div>

            {createMode === 'AUTO' ? (
              <RadioGroup
                value={autoBy}
                onValueChange={(v) => setAutoBy(v === 'rooms' ? 'rooms' : 'group')}
              >
                <div className="flex items-center gap-3">
                  <RadioGroupItem value="group" id="by-group" />
                  <Label htmlFor="by-group" className="flex items-center gap-2">
                    Человек на зал
                    <Input
                      type="number"
                      min={2}
                      max={50}
                      className="h-8 w-20"
                      value={groupSize}
                      disabled={autoBy !== 'group'}
                      onChange={(e) => setGroupSize(Math.max(2, Number(e.target.value) || 2))}
                    />
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <RadioGroupItem value="rooms" id="by-rooms" />
                  <Label htmlFor="by-rooms" className="flex items-center gap-2">
                    Число залов
                    <Input
                      type="number"
                      min={1}
                      max={50}
                      className="h-8 w-20"
                      value={roomCount}
                      disabled={autoBy !== 'rooms'}
                      onChange={(e) => setRoomCount(Math.max(1, Number(e.target.value) || 1))}
                    />
                  </Label>
                </div>
              </RadioGroup>
            ) : (
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm">
                  Число залов
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    className="h-8 w-20"
                    value={roomCount}
                    onChange={(e) => setRoomCount(Math.max(1, Number(e.target.value) || 1))}
                  />
                </Label>
                <p className="text-xs text-muted-foreground">
                  В превью распределите участников по залам перетаскиванием, затем
                  подтвердите.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Отмена
            </Button>
            <Button disabled={previewMutation.isPending} onClick={handlePreview}>
              {previewMutation.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <DoorOpen className="mr-1 h-4 w-4" />
              )}
              Показать распределение
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Превью распределения (dry-run) — подтверждение перед созданием */}
      <Dialog
        open={previewOpen}
        onOpenChange={(o) => {
          setPreviewOpen(o);
          if (!o) {
            setPreview(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <ModalHead
            icon={Users}
            title="Превью распределения"
            description={
              preview
                ? `${preview.viewerCount} участник(ов) → ${previewRooms.length} зал(ов). ` +
                  (createMode === 'MANUAL'
                    ? 'Перетащите участников между залами. Залы создаются только после подтверждения.'
                    : 'Залы ещё не созданы — участники будут перенесены только после подтверждения.')
                : 'Расчёт распределения…'
            }
          />
          {createMode === 'MANUAL' ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragStart={(e) => {
                const uid = e.active.data.current?.userId;
                setPreviewActiveUserId(typeof uid === 'number' ? uid : null);
              }}
              onDragEnd={handlePreviewDragEnd}
            >
              <div className="grid max-h-[50vh] gap-3 overflow-y-auto py-2 sm:grid-cols-2">
                {previewRooms.map((room) => (
                  <PreviewRoomColumn
                    key={room.roomIndex}
                    roomIndex={room.roomIndex}
                    label={room.label}
                    participants={room.participants}
                  />
                ))}
              </div>
              <DragOverlay>
                {previewActiveParticipant ? (
                  <div className="flex items-center gap-2 rounded-md border bg-card px-2.5 py-1.5 text-sm shadow-lg">
                    <Users className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{previewName(previewActiveParticipant)}</span>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          ) : (
            <div className="grid max-h-[50vh] gap-3 overflow-y-auto py-2 sm:grid-cols-2">
              {preview?.rooms.map((room) => (
                <div key={room.roomIndex} className="rounded-lg border bg-muted/30">
                  <div className="flex items-center gap-2 border-b p-2.5">
                    <span className="flex-1 text-sm font-medium">{room.label}</span>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      {room.participants.length}
                    </span>
                  </div>
                  <div className="space-y-1.5 p-2">
                    {room.participants.length === 0 ? (
                      <div className="py-4 text-center text-xs text-muted-foreground">
                        Пусто
                      </div>
                    ) : (
                      room.participants.map((p) => (
                        <div
                          key={p.userId}
                          className="flex items-center gap-2 rounded-md border bg-card px-2.5 py-1.5 text-sm"
                        >
                          <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <span className="flex-1 truncate">
                            {p.firstName?.trim() ||
                              (p.username ? `@${p.username}` : `#${p.userId}`)}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPreviewOpen(false);
                setPreview(null);
                setCreateOpen(true);
              }}
              disabled={openMutation.isPending}
            >
              Назад
            </Button>
            <Button disabled={openMutation.isPending} onClick={handleConfirmOpen}>
              {openMutation.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <DoorOpen className="mr-1 h-4 w-4" />
              )}
              Подтвердить и создать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Подтверждение закрытия */}
      <Dialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <DialogContent>
          <ModalHead
            icon={X}
            title="Закрыть сессионные залы"
            description="Все участники будут автоматически возвращены на просмотр трансляции."
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmCloseOpen(false)}
              disabled={closeMutation.isPending}
            >
              Отмена
            </Button>
            <Button variant="destructive" disabled={closeMutation.isPending} onClick={handleClose}>
              <X className="mr-1 h-4 w-4" />
              Закрыть залы
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
